import React, { useEffect, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import styled from "styled-components";
import * as Yup from "yup";
import SkeletonLoaderBasic from "../../../loaders/SkeletonLoaderBasic";
import {
  GetPCTemplate,
  GetTemplateListFilters,
  UpdatePCTemplate,
} from "../../../services/ProjectCenterServices";
import Button from "../../common/Button/Button";
import FormLabel from "../../common/forms/FormLabel";
import FormInput from "../../common/forms/FormInput";
import { selectStyles as sharedSelectStyles } from "../../common/forms/selectStyles";
import PermissionsGate from "../../../utils/permissionGate";
import {emptyMilestone, emptyResource} from "../utils/ProjectCenterUtils";

const validationSchema = Yup.object({
  name: Yup.string().required("Template Name is required").max(100, "Max 100 characters").trim(),
  description: Yup.string().nullable().max(255, "Max 255 characters"),
  department: Yup.string().nullable(),
  milestones: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("At least one milestone is required").trim(),
      })
    )
});

function PCTemplateDetails({ id,  templateDetails, loading, setLoading, setReload, reload }) {
  const history = useHistory();
  const [disabled, setDisabled] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    const getFilters = async () => {
      try {
        const res = await GetTemplateListFilters(source.token);
        if (res.status === 200) {
          const deptList = res.data?.data || [];
          setDepartmentOptions(
            deptList.map((dept) => ({ value: dept.value, label: dept.label }))
          );
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      }
    };

    getFilters();

    return () => {
      source.cancel("Component unmounted");
    };
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: templateDetails?.name || "",
      description: templateDetails?.description || "",
      department: templateDetails?.department || "",
      milestones: templateDetails?.milestones?.length
        ? templateDetails.milestones.map((s) => ({ id: s.id, name: s.name, sequence: s.sequence }))
        : [emptyMilestone()],
      resources: templateDetails?.resources?.length
        ? templateDetails.resources.map((l) => ({
          id: l.id,
          name: l.name,
          is_required: Boolean(l.is_required),
          sequence: l.sequence,
        }))
        : [],
    },
    validationSchema,
    onSubmit: async (values) => {
      setDisabled(true);

      const payload = {
        name: values.name,
        description: values.description,
        department: values.department || null,
        milestones: values.milestones.map((s, index) => ({
          id: s.id,
          name: s.name,
          sequence: index + 1,
        })),
        resources: values.resources.map((l, index) => ({
          id: l.id,
          name: l.name,
          is_required: l.is_required ? 1 : 0,
          sequence: index + 1,
        })),
      };

      try {
        if (id > 0) {
          payload.id = id;
        }
        const newId = await UpdatePCTemplate(payload);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Template created/updated successfully",
        });
        if (!Boolean(id)) {
          newId?.data?.data ? history.push(`/project-center/templates/open/${newId?.data?.data}`) : history.push(`/project-center/templates/table`);
        }else{
          setReload(!reload);
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something went wrong",
        });
      } finally {
        setDisabled(false);
      }
    },
  });

  const addMilestone = () => {
    formik.setFieldValue("milestones", [...formik.values.milestones, emptyMilestone()]);
  };

  const removeMilestone = (index) => {
    const updated = formik.values.milestones.filter((_, i) => i !== index);
    formik.setFieldValue("milestones", updated.length ? updated : [emptyMilestone()]);
  };

  const moveMilestone = (index, direction) => {
    const updated = [...formik.values.milestones];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    formik.setFieldValue("milestones", updated);
  };

  const addLink = () => {
    formik.setFieldValue("resources", [...formik.values.resources, emptyResource()]);
  };

  const removeLink = (index) => {
    const updated = formik.values.resources.filter((_, i) => i !== index);
    formik.setFieldValue("resources", updated.length ? updated : []);
  };


  if (loading) {
    return <SkeletonLoaderBasic />;
  }

  return (
    <div className="card card-profile-info-card filter-table-bg">
      <div className="new-card-header">
        <div className="card-header pt-0">
          {id > 0 ? "Edit Template" : "Add Template"}{" "}
          {disabled ? <i className="fas fa-cog fa-spin" /> : ""}
        </div>
        <div className="card-body-inr card-body-info">
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel required>Template Name</FormLabel>
                  <FormInput
                    {...formik.getFieldProps("name")}
                    error={formik.errors.name}
                    touched={formik.touched.name}
                    placeholder="Enter template name"
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel>Department</FormLabel>
                  <Select
                    options={departmentOptions}
                    value={
                      departmentOptions.find(
                        (opt) => opt.value === formik.values.department
                      ) || null
                    }
                    onChange={(opt) =>
                      formik.setFieldValue("department", opt?.value || "")
                    }
                    placeholder="Select department..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <FormLabel>Description</FormLabel>
                  <FormInput
                    {...formik.getFieldProps("description")}
                    error={formik.errors.description}
                    touched={formik.touched.description}
                    placeholder="Enter description"
                  />
                </div>
              </div>
            </div>

            <hr />

            <div className="row mb-2">
              <div className="col-12 d-flex align-items-center justify-content-between">
                <div className="card-header pt-0">Milestones</div>
                <Button
                  type="button"
                  title="Add Milestone"
                  onClick={addMilestone}
                  className="btn-save btn-success"
                >
                  <i className="fal fa-plus"></i> Add Milestone
                </Button>
              </div>
            </div>

            {typeof formik.errors.milestones === "string" && (
              <div className="invalid-feedback d-block mb-2">
                {formik.errors.milestones}
              </div>
            )}

            {formik.values.milestones.map((milestone, index) => (
              <div className="row align-items-center mb-2" key={index}>
                <div className="col">
                  <FormInput
                    name={`milestones[${index}].name`}
                    value={milestone.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.milestones?.[index]?.name}
                    touched={formik.touched.milestones?.[index]?.name}
                    placeholder="Milestone name"
                  />
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-primary rounded-circle"
                    title="Move Up"
                    disabled={index === 0}
                    onClick={() => moveMilestone(index, -1)}
                  >
                    <i className="fal fa-arrow-up"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary rounded-circle ml-1"
                    title="Move Down"
                    disabled={index === formik.values.milestones.length - 1}
                    onClick={() => moveMilestone(index, 1)}
                  >
                    <i className="fal fa-arrow-down"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger rounded-circle ml-1"
                    title="Remove Milestone"
                    onClick={() => removeMilestone(index)}
                  >
                    <i className="fal fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}

            <hr />

            <div className="row mb-2">
              <div className="col-12 d-flex align-items-center justify-content-between">
                <div className="card-header pt-0">Resources</div>
                <Button
                  type="button"
                  title="Add Resource"
                  onClick={addLink}
                  className="btn-save btn-success"
                >
                  <i className="fal fa-plus"></i> Add Resource
                </Button>
              </div>
            </div>

            {formik.values.resources.map((link, index) => (
              <div className="row align-items-center mb-2" key={index}>
                <div className="col">
                  <FormInput
                    name={`resources[${index}].name`}
                    value={link.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.resources?.[index]?.name}
                    touched={formik.touched.resources?.[index]?.name}
                    placeholder="Link name (e.g. Jira, Design)"
                  />
                </div>
                <div className="col-auto">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`link_required_${index}`}
                      checked={link.is_required}
                      onChange={(e) =>
                        formik.setFieldValue(
                          `resources[${index}].is_required`,
                          e.target.checked
                        )
                      }
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`link_required_${index}`}
                    >
                      Required
                    </label>
                  </div>
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-danger rounded-circle"
                    title="Remove Resource"
                    onClick={() => removeLink(index)}
                  >
                    <i className="fal fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}

            <hr />

            <div className="form-group form-group-save-cancel mt-4">
              {id > 0 ? (
                <PermissionsGate
                  scopes={["projectcentertemplatesedit"]}
                  RenderError={() => (
                    <button
                      className="btn btn-save btn-success"
                      type="submit"
                      title="Save"
                      disabled
                    >
                      <i className="fal fa-save"></i>
                      Save
                    </button>
                  )}
                >
                  <button
                    className="btn btn-save btn-success"
                    type="submit"
                    title="Save"
                    disabled={disabled}
                  >
                    {disabled ? (
                      <i className="fas fa-cog fa-spin"></i>
                    ) : (
                      <i className="fal fa-save"></i>
                    )}
                    Save
                  </button>
                </PermissionsGate>
              ) : (
                <button
                  className="btn btn-save btn-success"
                  type="submit"
                  title="Save"
                  disabled={disabled}
                >
                  {disabled ? (
                    <i className="fas fa-cog fa-spin"></i>
                  ) : (
                    <i className="fal fa-save"></i>
                  )}
                  Save
                </button>
              )}

              <button
                className="btn btn-close btn-danger"
                type="button"
                title="Cancel"
                onClick={() => {formik.resetForm()}}
              >
                <i className="fal fa-times"></i>Cancel
              </button>
              {Object.keys(formik.values).map((key, ind) => {
                  if (formik.touched[key] && formik.errors[key]) {
                  return (
                      <><div className="invalid-feedback d-block" key={ind}>
                      {Array.isArray(formik.errors[key]) ? 
                      (formik.errors[key].forEach(element => {
                        return element?.name || element
                      })) : formik.errors[key]}
                      </div>
                      </>
                  )
                  }
                })}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PCTemplateDetails;